/**
 * 저장이 막혔을 때 하는 일.
 *
 * 화면은 「필수입력확인: 호기를 확인하십시요」처럼 **위치만** 준다. 관측값도, 허용되는
 * 대안도 없다. 연구(VeriHarness, arXiv 2607.14167)는 그 둘을 붙였을 때 +42~44pp,
 * 위치만으로는 베이스라인이라고 잰다. 여기서 그 둘을 우리가 만들어 붙인다.
 *
 * `index.ts` 를 모른다. 화면·코드표·`options()` 를 인자로 받는다 — 그래야 단일 초안
 * 경로와 걸음 경로가 같은 것을 부르고, 시험이 화면 없이 돈다.
 */
import { objectParticle, topicParticle } from './korean';
import { codeListFor } from './screenIndex';
import type { AgentField, AgentScreen, CodeLists } from './screenIndex';

export type BlockKind = 'typed' | 'listed' | 'popup' | 'row' | 'unknown';

export type Blocked = {
  /** 화면 통지 원문. 사람이 승인 대화에서 보는 그 글자. */
  notice: string;
  /** 통지가 댄 칸 이름(화면 말). 못 대면 ''. */
  label: string;
  /** 카탈로그 칸 키. 못 찾으면 undefined. */
  field?: string;
  /** 통지의 동사가 아니라 칸의 계약에서 나온다. */
  kind: BlockKind;
  /** 지금 초안에 든 값. 없으면 ''. */
  observed: string;
  /** 허용되는 대안. 아직 안 읽었거나 못 읽었으면 []. */
  alternatives: string[];
  source: 'catalog' | 'codelist' | 'combo' | 'popup' | 'none';
  /**
   * 대안이 비었을 때 **왜** 비었는지. `팝업이 안 열렸다` 면 「고를 게 없다」가 아니라
   * 「못 열었다」이므로, 저장 뒤 경로가 다른 말을 한다. `options()` 가 실어 온다.
   */
  reason?: string;
  /** 같은 차단인지 가르는 서명. 통지 집합을 정렬해 붙인 것. */
  signature: string;
};

export type Choice =
  | { kind: 'pick'; value: string; why: string }
  | { kind: 'ask'; question: string; options: string[] };

/** 스펙 §5.4. 바꾸지 말고 보고하라. */
export const SAME_BLOCK_MAX = 1;
export const BLOCKS_PER_TURN = 3;
export const SAVE_ATTEMPTS = 3;

export type ResolveDeps = {
  route: string;
  screen?: AgentScreen;
  codeLists?: CodeLists;
  /**
   * 화면에서 읽은 후보. 팝업이 안 열리면 `[]` + `reason` 을 실어 온다 — 「고를 게 없다」와
   * 「못 열었다」를 가른다. 옛 자리(시험 픽스처)는 `string[]` 만 주므로 둘 다 받는다.
   */
  options?: (
    request: { routePath: string; field: string },
  ) => Promise<string[] | { options: string[]; reason?: string }>;
};

/** `options()` 의 옛 모양(`string[]`)과 새 모양(`{options,reason}`)을 하나로 맞춘다. */
export function normalizeOptions(
  got: string[] | { options: string[]; reason?: string },
): { options: string[]; reason?: string } {
  return Array.isArray(got) ? { options: got } : got;
}

/** 서명의 구분자. 눈에 안 보이는 글자라 통지 본문과 절대 안 겹친다. */
const SEPARATOR = String.fromCharCode(1);

/** 통지 집합의 서명. 순서와 공백을 무시한다. */
export function signatureOf(notices: string[]): string {
  return Array.from(new Set(notices.map((one) => one.replace(/\s+/g, ' ').trim())))
    .sort().join(SEPARATOR);
}

/**
 * 칸 이름에서 **화면이 붙인 도움말**을 떼어 낸다.
 *
 * 지출결의서의 비고는 카탈로그에 `비고 (최대 10줄까지 입력가능합니다)` 로 들어 있다.
 * 그대로 답에 실으면 `비고 (최대 10줄까지 입력가능합니다)=오늘 …` 이 나간다.
 * 괄호 안이 안내문일 때만 뗀다 — `고객명(현장/민원신고자)` 처럼 뜻을 가르는 괄호는 남긴다.
 */
export function plainLabel(label: string): string {
  return label.replace(/\s*\((?=[^)]*(?:입력|가능|최대|자리|형식|단위|참고))[^)]*\)\s*$/, '').trim() || label;
}

/** 글자를 정렬해 맞춘다. 화면 안내 `고장상세부위` 와 카탈로그 `고장부위상세` 는 같은 칸이다. */
export function labelKey(text: string): string {
  return text.replace(/[^0-9A-Za-z가-힣]/g, '').split('').sort().join('');
}

/**
 * 화면 통지와 카탈로그가 **꼬리 한 마디만 다르게** 부르는 자리.
 *
 * 통지는 `호기를 확인하십시요` 라 하고 카탈로그에는 `호기명` 으로 적혀 있다. 현장도
 * 마찬가지다(`현장` / `현장명`). 글자를 정렬해 맞춰도 이건 안 맞는다 — 한쪽에만 있는
 * 글자라서다. `명`·`코드` 만 뗀다. 남는 말이 한 글자면 떼지 않는다(`성명` → `성`).
 */
function coreLabel(text: string): string {
  const trimmed = text.replace(/(명|코드)$/, '');
  return trimmed.length >= 2 ? trimmed : text;
}

/**
 * 통지에서 칸 이름만 뽑는다. 동사는 **버린다** — 종류 판정에 쓰지 않는다.
 *
 * 스펙 §5.1: 「입력·확인·선택」은 칸 이름을 뽑는 데만 쓴다. `호기를 **확인**하십시요`
 * 를 「쳐 넣는 칸」으로 읽는 바람에 같은 저장을 다섯 번 눌렀다.
 */
const NAMED = /필수입력확인:\s*(.+?)(?:을|를|이|가)?\s*(?:입력|확인|선택|기재)/;

/**
 * `경고:`·`오류:` 로 시작하는 통지. 제품코드는 `경고: 보관창고를 더블클릭하여 입력해 주세요`,
 * 거래처는 `오류: 이메일을 입력해주세요` 라 했다(2026-09-25) — 이름을 못 읽어 묻지도 못했다.
 * 이 머리말은 칸이 아닌 일에도 붙으므로 좁게 읽는다 — 띄어쓰기 없는 한 낱말, 목적격 조사,
 * 그리고 입력·선택·확인만(발주는 `경고: 발주처를 확인해주세요`). `삭제할 행을 선택하세요` 는 칸 이름이 아니다.
 */
const WARNED = /(?:경고|오류)\s*:\s*([^\s:]+?)(?:을|를)\s*(?:더블클릭하여\s*)?(?:입력|선택|확인)/;

function nameIn(notice: string): string {
  return (NAMED.exec(notice)?.[1] ?? WARNED.exec(notice)?.[1] ?? '').trim();
}

/**
 * 통지들이 이름을 댄 칸들. 순서대로, 중복 없이.
 *
 * `index.ts` 가 같은 일을 하는 정규식을 **따로 하나 더** 들고 있었다. 그쪽은 조사를
 * 필수로 보고 `기재` 를 몰라서, `필수입력확인: 호기 확인하십시요` 를 「이름을 못 댄
 * 거절」로 읽고 모델에게 되돌렸다. 여기 하나만 둔다.
 */
export function namedLabels(notices: string[]): string[] {
  const found = notices
    .map(nameIn)
    .filter((name): name is string => Boolean(name));
  return Array.from(new Set(found));
}

/** 칸의 계약이 종류를 정한다. 스펙 §5.1 의 판정 순서 그대로, 위에서부터 처음 맞는 것. */
function kindOf(
  field: AgentField | undefined,
  codeLists: CodeLists | undefined,
  screen?: AgentScreen,
): BlockKind {
  if (!field) return 'unknown';
  if (field.searchPopup) return 'popup';
  if (field.options?.length || field.optionCodes?.length) return 'listed';
  if (codeListFor(field, codeLists)?.items?.length) return 'listed';
  if (/combo|select|radio/i.test(field.widget)) return 'listed';
  const inTab = (screen?.tabs ?? [])
    .some((tab) => Object.prototype.hasOwnProperty.call(tab.labels ?? {}, field.key));
  if (inTab) return 'row';
  return 'typed';
}

export function classify(
  notices: string[],
  screen: AgentScreen | undefined,
  codeLists: CodeLists | undefined,
  draft: Record<string, string>,
): Blocked[] {
  const signature = signatureOf(notices);
  const fields = screen?.draftFields ?? [];
  const found: Blocked[] = [];
  for (const notice of notices) {
    const label = nameIn(notice);
    if (!label) continue;
    const named = (one: { label: string | null; key: string }) => plainLabel(one.label ?? one.key);
    // 또렷한 이름이 먼저다. 꼬리를 뗀 맞춤은 그것이 없을 때만 본다.
    const field = fields.find((one) => labelKey(named(one)) === labelKey(label))
      ?? fields.find((one) => labelKey(coreLabel(named(one))) === labelKey(coreLabel(label)));
    // 같은 칸을 두 번 세지 않는다. 화면이 같은 요구를 두 통지로 내는 일이 있다.
    if (found.some((one) => one.label === label)) continue;
    found.push({
      notice,
      label,
      field: field?.key,
      kind: kindOf(field, codeLists, screen),
      observed: String(draft[field?.key ?? ''] ?? '').trim(),
      alternatives: [],
      source: 'none',
      signature,
    });
  }
  return found;
}

/**
 * 스펙 §5.2. 막힌 칸의 **허용되는 대안**을 읽어 붙인다.
 *
 * 새로 짓는 것이 없다 — 카탈로그·코드표·화면의 `options()` 를 종류에 따라 부를 뿐이다.
 * **초안은 열린 채로 부른다.** 호기 후보는 현장을 채운 뒤에야 나오므로, 초안을 버리고
 * 다시 열면 그 현장의 호기가 아니라 아무 호기도 안 나온다.
 */
export async function resolve(blocked: Blocked, deps: ResolveDeps): Promise<Blocked> {
  const field = (deps.screen?.draftFields ?? []).find((one) => one.key === blocked.field);
  if (!field || blocked.kind === 'typed' || blocked.kind === 'unknown') {
    return { ...blocked, alternatives: [], source: 'none' };
  }
  if (blocked.kind === 'listed') {
    if (field.options?.length) return { ...blocked, alternatives: [...field.options], source: 'catalog' };
    if (field.optionCodes?.length) {
      return { ...blocked, alternatives: field.optionCodes.map((one) => one.label), source: 'catalog' };
    }
    const listed = codeListFor(field, deps.codeLists)?.items?.map((one) => one.name).filter(Boolean);
    if (listed?.length) return { ...blocked, alternatives: listed, source: 'codelist' };
  }
  // popup · row · 목록 없는 콤보: 화면에 묻는다.
  const probe: { options: string[]; reason?: string } = deps.options
    ? await deps.options({ routePath: deps.route, field: field.key })
      .then(normalizeOptions)
      // 팝업이 안 열렸다. **후보를 지어내지 않는다** — 빈 대안은 §5.3 이 「대안 없음」으로 받는다.
      .catch(() => ({ options: [] as string[] }))
    : { options: [] };
  const asked = probe.options.map((one) => one.trim()).filter(Boolean);
  if (asked.length) {
    return {
      ...blocked,
      alternatives: Array.from(new Set(asked)),
      source: blocked.kind === 'listed' ? 'combo' : 'popup',
    };
  }
  // 화면이 못 열어 줬어도 카탈로그가 그 목록을 아는 수가 있다 — 고장부위가 그렇다.
  // 앞 칸에 매달린 목록은 `codeListFor` 가 스스로 비켜 준다(`popupArgs === null`).
  if (field.options?.length) return { ...blocked, alternatives: [...field.options], source: 'catalog' };
  if (field.optionCodes?.length) {
    return { ...blocked, alternatives: field.optionCodes.map((one) => one.label), source: 'catalog' };
  }
  const known = codeListFor(field, deps.codeLists)?.items?.map((one) => one.name).filter(Boolean);
  if (known?.length) return { ...blocked, alternatives: known, source: 'codelist' };
  // 카탈로그도 코드표도 못 줬다. 화면이 「왜」 비었는지 알렸으면 실어 둔다 —
  // 저장 뒤 경로가 팝업이 안 열린 것을 「고를 게 없다」와 다르게 말할 수 있게.
  return {
    ...blocked, alternatives: [], source: 'none', ...(probe.reason ? { reason: probe.reason } : {}),
  };
}

/** 단추로 내밀 후보의 상한. 스무 개가 넘으면 사람이 읽지 않는다. */
const MAX_OPTIONS = 20;

const WORDY = /[^0-9A-Za-z가-힣]/g;

function normalized(text: string): string {
  return text.replace(WORDY, '').toLocaleLowerCase();
}

/** 말에서 후보와 견줄 조각. 2글자 이상, 조사·어미가 붙은 채여도 앞머리는 남는다. */
function tokensOf(spoken: string): string[] {
  return spoken.split(/[\s,.·/]+/).map(normalized).filter((one) => one.length >= 2);
}

function escaped(letter: string): string {
  return letter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 후보의 글자가 말 안에 **낱말째로** 들어 있는가.
 *
 * 두 가지를 함께 봐야 한다. 하나는 구분자다 — 화면의 `CEN-200AV - 6CH` 와 사람이 친
 * `CEN-200AV 6CH` 는 같은 것이므로 사이의 기호를 건너뛰며 맞춘다. 다른 하나는 경계다 —
 * 그냥 글자 포함으로 보면 `부품 2` 가 `부품 27 넣어줘` 안에 들어 있다고 나와서 27번을
 * 2번으로 사 온다. 숫자 뒤에 숫자가, 영문 뒤에 영문이 이어지면 그것은 다른 낱말이다.
 * 한글에는 이 빗장을 걸지 않는다. `가나공장에서`처럼 조사가 그대로 붙는다.
 */
function saidWhole(spoken: string, candidate: string): boolean {
  const letters = candidate.replace(WORDY, '');
  if (letters.length < 2) return false;
  const gap = '[^0-9A-Za-z가-힣]*';
  const body = letters.split('').map(escaped).join(gap);
  // 경계는 **일치 위치의 이웃 글자**로 본다. 전에는 뒤돌아보기 단언으로 봤는데,
  // 그것은 구형 Safari 가 정규식을 **만드는 순간** SyntaxError 로 뱉는다 — 동작이 아니라
  // 파싱이라 try 로도 안 막힌다. 모양은 같다: 숫자 뒤에 숫자가, 영문 뒤에 영문이
  // 이어지면 다른 낱말이다. 한글은 조사가 그대로 붙으므로 빗장을 걸지 않는다.
  const family = (letter: string | undefined): string => {
    if (!letter) return '';
    if (/[0-9]/.test(letter)) return 'digit';
    if (/[A-Za-z]/.test(letter)) return 'latin';
    return '';
  };
  const head = family(letters[0]);
  const tail = family(letters[letters.length - 1]);
  const finder = new RegExp(body, 'gi');
  for (let hit = finder.exec(spoken); hit; hit = finder.exec(spoken)) {
    const before = family(spoken[hit.index - 1]);
    const after = family(spoken[hit.index + hit[0].length]);
    if (!(head && before === head) && !(tail && after === tail)) return true;
  }
  return false;
}

/**
 * 스펙 §5.3. 사람이 말한 것과 대안을 견줘 **고르거나 묻는다.**
 *
 * 고르는 쪽이 먼저인 이유는 사람의 시간이다 — 옆자리 동료도 고를 수 있으면 고르고
 * 정말 모를 때만 묻는다. 묻는 것은 언제나 **단추**다. 글에 후보를 적으면 사람은 다시
 * 타이핑해야 한다.
 */
export function choose(spoken: string, blocked: Blocked): Choice {
  const alternatives = blocked.alternatives;
  const tokens = tokensOf(spoken);
  // 1. 후보 글자가 말에 통째로 들어 있는 것.
  const contained = alternatives.filter((one) => saidWhole(spoken, one));
  if (contained.length === 1) return { kind: 'pick', value: contained[0]!, why: '말에 그대로 들어 있다' };
  // 2. 말의 조각이 후보 하나에만 붙는 것. 조각이 여러 후보에 붙으면 모호하다.
  if (contained.length === 0) {
    const hit = alternatives.filter((one) => tokens.some((token) => normalized(one).includes(token)));
    if (hit.length === 1) return { kind: 'pick', value: hit[0]!, why: '말의 낱말이 이 후보에만 붙는다' };
  }
  // 3. 고를 게 하나뿐이다. 묻는 것이 사람을 방해하는 것이다.
  if (alternatives.length === 1) return { kind: 'pick', value: alternatives[0]!, why: '후보가 하나뿐이다' };
  // 4. 묻는다. 말과 많이 겹치는 것을 앞에 — 상한에 잘려도 답이 남아 있게.
  const overlap = (one: string) => tokens.filter((token) => normalized(one).includes(token)).length;
  const ranked = alternatives.slice().sort((left, right) => overlap(right) - overlap(left));
  return {
    kind: 'ask',
    question: `${topicParticle(blocked.label)} 어느 것입니까?`,
    options: ranked.slice(0, MAX_OPTIONS),
  };
}

/**
 * 한 턴의 차단 지갑.
 *
 * 저장을 다섯 번 누른 이유는 **차단을 세는 지갑이 없어서**다. `MAX_ROUNDS` 는 계획
 * 라운드를 세지 차단을 세지 않는다. 둘은 다른 것이다(스펙 §5.4).
 */
export class BlockLedger {
  private readonly seen = new Map<string, number>();

  private readonly perStep = new Map<string, number>();

  private distinct = 0;

  saves = 0;

  /** 서로 다른 차단으로 이미 센 서명. 같은 것을 두 번 세지 않는다. */
  private readonly counted = new Set<string>();

  /**
   * **세지 않고 엿본다.** 이 서명을 지금까지 몇 번 만났나.
   *
   * 세는 것은 결과를 안 뒤여야 한다 — 고르기로 풀린 차단인지 아닌지가 그때 갈린다.
   */
  peek(signature: string): number {
    return this.seen.get(signature) ?? 0;
  }

  /**
   * 이 서명을 또 시도해도 되는가. 처음이면 true, 재시도 1회까지 true, 그다음 false.
   *
   * `settled` 는 **고르기로 넘긴 차단**이다. 같은 서명은 그대로 세되(`SAME_BLOCK_MAX`)
   * 서로 다른 차단의 지갑(`BLOCKS_PER_TURN`)은 먹지 않는다 — 고르기로 풀린 차단이
   * 지갑을 먹으면 네 걸음짜리 일이 셋에서 멈춘다(스펙 §5.4).
   */
  allows(signature: string, settled = false): boolean {
    const count = this.seen.get(signature) ?? 0;
    if (!settled && !this.counted.has(signature)) {
      this.counted.add(signature);
      this.distinct += 1;
    }
    this.seen.set(signature, count + 1);
    return count <= SAME_BLOCK_MAX;
  }

  /** 서로 다른 차단이 `BLOCKS_PER_TURN` 을 넘었는가. */
  exhausted(): boolean {
    return this.distinct > BLOCKS_PER_TURN;
  }

  /** 저장을 한 번 더 눌러도 되는가. */
  allowsSave(): boolean {
    this.saves += 1;
    return this.saves <= SAVE_ATTEMPTS;
  }

  /**
   * **그 걸음의** 저장을 한 번 더 눌러도 되는가.
   *
   * 걸음 경로도 이 지갑을 쓴다(스펙 §5.4). 다만 세는 단위가 걸음이다 — 인사등록은
   * 기본정보·가족·자격증을 각각 저장하므로 걸음 셋에 저장 셋이 정상이다. 턴 총합으로
   * 세면 정상인 화면이 마지막 걸음에서 막힌다. 막아야 하는 것은 **한 걸음이 같은
   * 이유로 세 번 넘게 되풀이되는 것**이다.
   */
  allowsSaveFor(step: string): boolean {
    const count = (this.perStep.get(step) ?? 0) + 1;
    this.perStep.set(step, count);
    return count <= SAVE_ATTEMPTS;
  }
}

export type Settled =
  | {
    kind: 'filled';
    fields: Record<string, string>;
    said: string[];
    /**
     * 그 칸에서 **읽은 허용 목록**. 다시 넣을 때 그대로 `allowed` 로 넘긴다.
     *
     * 카탈로그에 없어 팝업으로만 알 수 있는 목록이라(호기가 그렇다) 이때 안 넘기면
     * 드라이버는 우리가 고른 값을 검증 없이 쳐 넣는다.
     */
    allowed: Record<string, string[]>;
  }
  | { kind: 'ask'; question: string; options: string[]; field: string }
  | { kind: 'replan'; note: string }
  | { kind: 'stop'; reason: string };

export type SettleInput = {
  notices: string[];
  spoken: string;
  draft: Record<string, string>;
  deps: ResolveDeps;
  ledger: BlockLedger;
  /** 이미 「사용자 말에 있으면 채워라」로 한 번 다시 짠 라벨들. 두 번 짜지 않는다. */
  demanded: Set<string>;
};

/**
 * 스펙 §6. 차단 하나를 만났을 때 다음에 할 일 하나를 정한다.
 *
 * 순서가 곧 규칙이다 — 대안을 읽을 수 있으면 먼저 읽어 고르고, 없으면 한 번 다시 짜고,
 * 같은 서명이 두 번째면 묻고, 지갑이 비면 멈춘다.
 */
export async function settleBlocked(input: SettleInput): Promise<Settled> {
  const blocks = classify(input.notices, input.deps.screen, input.deps.codeLists, input.draft);
  // 화면이 칸 이름을 안 댔다. 여기서 정할 것이 없으니 부른 쪽이 통지 원문으로 판단한다.
  if (!blocks.length) return { kind: 'stop', reason: input.notices.join(' / ') };
  const signature = blocks[0]!.signature;
  const labelled = () => blocks.map((one) => one.label).join(', ');
  // **엿보고 판정하고, 세는 것은 맨 뒤에서 한다.** 고르기로 풀린 차단은 지갑을 먹지
  // 않아야 하는데(§5.4), 풀렸는지는 대안을 읽어 봐야 안다.
  const again = input.ledger.peek(signature) >= SAME_BLOCK_MAX;
  if (input.ledger.exhausted()) return { kind: 'stop', reason: labelled() };

  const resolved = await Promise.all(blocks.map((one) => resolve(one, input.deps)));
  const readable = resolved.filter((one) => one.alternatives.length && one.field);
  const unresolved = resolved.filter((one) => !one.alternatives.length);

  // **고른 값으로 저장했는데 같은 통지가 또 왔다.** 화면이 우리 고름을 거절한 것이므로
  // 다시 고르지 않는다 — 같은 근거로 또 고르면 같은 자리에서 또 막힌다(§5.4·§6.4).
  // 대안이 하나뿐이어도 묻는다. 그 하나가 방금 거절당한 값이다.
  if (again && readable.length) {
    const one = readable[0]!;
    input.ledger.allows(signature);
    return {
      kind: 'ask',
      question: `${topicParticle(one.label)} 어느 것입니까?`,
      options: one.alternatives.slice(0, MAX_OPTIONS),
      field: one.field!,
    };
  }

  const picks: Record<string, string> = {};
  const allowed: Record<string, string[]> = {};
  const said: string[] = [];
  for (const one of readable) {
    const decided = choose(input.spoken, one);
    if (decided.kind === 'ask') {
      input.ledger.allows(signature);
      return {
        kind: 'ask', question: decided.question, options: decided.options, field: one.field!,
      };
    }
    picks[one.field!] = decided.value;
    allowed[one.field!] = [...one.alternatives];
    said.push(`${topicParticle(one.label)} ${decided.value} 로 넣었습니다`);
  }
  if (Object.keys(picks).length && !unresolved.length) {
    // 이 차단은 우리가 풀었다. 서명은 세되 **지갑은 먹지 않는다**(§5.4).
    input.ledger.allows(signature, true);
    return { kind: 'filled', fields: picks, said, allowed };
  }
  input.ledger.allows(signature);
  if (input.ledger.exhausted()) return { kind: 'stop', reason: labelled() };

  // 대안이 없는 칸이 남았다. 처음이면 사용자 말에서 채워 보게 한 번 다시 짠다.
  const fresh = unresolved.map((one) => one.label).filter((label) => !input.demanded.has(label));
  if (fresh.length && !again) {
    for (const label of fresh) input.demanded.add(label);
    return {
      kind: 'replan',
      note: `\n\n직전 초안은 저장이 막혔다: 화면이 ${fresh.join(', ')} 을(를) 요구한다.`
        + ' 사용자가 말한 것 중에 그 값이 있으면 채워 다시 내라.'
        + ' 사용자 말에 없으면 **그 칸을 비워 둬라** — 지어내지 마라.',
    };
  }
  // 두 번째다. 글자칸은 이름을 대고 묻는다. 읽을 수 있어야 할 칸이 여기 왔으면 읽기가 실패한 것이다.
  // **`row` 도 이름을 대고 묻는 쪽이다**(§6.4). 표 안의 칸은 사람이 값을 말해 주면 그만인데
  // 「목록을 못 읽었습니다」라고 하면 사무직은 무엇을 해야 할지 알 수 없다 — 자격증번호가
  // 그렇게 끝났다. 목록을 못 읽었다는 말은 `popup`·`listed` 에만 쓴다.
  const named = unresolved
    .filter((one) => one.kind === 'typed' || one.kind === 'unknown' || one.kind === 'row')
    .map((one) => one.label);
  const unreadable = unresolved.filter((one) => one.kind === 'popup' || one.kind === 'listed');
  // **팝업이 안 열린 것은 「못 읽었다」가 아니라 「못 열었다」다.** notFound 경로가 이미 쓰는
  // 그 문장을 저장 뒤 경로에도 준다 — 사무직은 「목록을 못 읽었습니다」로는 뭘 할지 모른다.
  const shut = unreadable.filter((one) => one.reason === '팝업이 안 열렸다').map((one) => one.label);
  // **읽었는데 안 맞은 것도 「못 읽었다」가 아니다.** 목록은 읽혔고 그중 사용자가 댄 이름과
  // 닮은 것이 없었다 — 그때 「읽지 못했습니다」라고 하면 사무직은 화면 탓으로 알고 같은 말을
  // 다시 시킨다. 정확한 이름을 청하는 것이 할 수 있는 일이다. 이 이유를 받는 세 자리
  // (단일 초안·걸음·저장 뒤)가 같은 말을 해야 한다.
  const unmatched = unreadable.filter((one) => one.reason === '비슷한 이름을 찾지 못했다');
  const READ_BUT_MISSED = ['팝업이 안 열렸다', '비슷한 이름을 찾지 못했다'];
  const unread = unreadable
    .filter((one) => !READ_BUT_MISSED.includes(one.reason ?? '')).map((one) => one.label);
  const parts = [
    named.length ? `${objectParticle(named.join(', '))} 알려 주시면 마저 채워 저장하겠습니다.` : '',
    unread.length ? `${unread.join(', ')}의 목록을 화면에서 읽지 못했습니다.` : '',
    // 초안에 든 값이 있으면 그것을 대고 말한다. 없으면 칸 이름만 댄다 — 지어내지 않는다.
    ...unmatched.map((one) => (one.observed
      ? `${topicParticle(one.observed)} ${one.label} 목록에서 찾지 못했습니다.`
      : `${one.label} 목록에서 비슷한 이름을 찾지 못했습니다.`)
      + ' 화면에 있는 정확한 이름을 알려 주시면 다시 해보겠습니다.'),
    ...shut.map((label) => `${topicParticle(label)} 고르는 창이 열리지 않았습니다. 잠시 뒤 다시 말씀해 주세요.`),
  ].filter(Boolean);
  return { kind: 'stop', reason: parts.join(' ') };
}
